import { Component, forwardRef, Input, OnInit, input, output, viewChild } from "@angular/core";
import { NgModel, NG_VALUE_ACCESSOR, FormsModule } from "@angular/forms";
import { SelectItem } from "primeng/api";
import { Observable, takeUntil } from "rxjs";
import { Vendor } from "../../../types/vendor";
import { PCPTradePartnerSelectorBaseComponent } from "../pcp-tradepartner-selector.basecomponent";

import { ErrormessageComponent } from "../../validation/errormessage/errormessage.component";
import { MultiSelectModule } from "primeng/multiselect";
import { FluidModule } from "primeng/fluid";

@Component({
  selector: "pcp-tradepartner-multiselect",
  templateUrl: "./pcp-tradepartner-multi-select.component.html",
  styleUrls: [],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      multi: true,
      useExisting: forwardRef(() => PCPTradePartnerMultiSelectComponent),
    }
  ],
  standalone: true,
  imports: [MultiSelectModule, FormsModule, FluidModule, ErrormessageComponent]
})
export class PCPTradePartnerMultiSelectComponent extends PCPTradePartnerSelectorBaseComponent implements OnInit {
  readonly tradePartnerSelection = viewChild<NgModel>("tradePartnerSelection");

  // TODO: Skipped for migration because:
  //  Accessor inputs cannot be migrated as they are too complex.
  @Input() set tradePartners(tradePartners: Vendor[]) {
    if (!tradePartners || tradePartners.length === 0) { return; }

    this._tradePartners = tradePartners;
    this.tradePartnerOptions = this.tradePartnersToOptions(tradePartners);
  }

  get tradePartners(): Vendor[] {
    return this._tradePartners;
  }

  _tradePartners: Vendor[] = [];

  _observable: Observable<Vendor[]>;

  // TODO: Skipped for migration because:
  //  Accessor inputs cannot be migrated as they are too complex.
  @Input() set tradePartnersObservable(observable: Observable<Vendor[]>) {
    this._observable = observable;
    this.reloadOptions();
  };

  get tradePartnersObservable(): Observable<Vendor[]> {
    return this._observable;
  }

  readonly placeholder = input<string>("Trade Partners");
  readonly maxSelectedLabels = input<number>(5);

  _selectedTradePartners: Vendor[] = [];

  // TODO: Skipped for migration because:
  //  Accessor inputs cannot be migrated as they are too complex.
  @Input() set selectedTradePartners(vendors: Vendor[]) {
    this._selectedTradePartners = vendors;
    this.propagateChange(this._selectedTradePartners);
  };

  get selectedTradePartners(): Vendor[] {
    return this._selectedTradePartners;
  }

  readonly selectedTradePartnersChange = output<Vendor[]>();

  get label(): string {
    if (this.isLoading) return this.loadingText;

    return this.placeholder();
  }

  tradePartnerOptions: SelectItem[];

  constructor() {
    super();
  }

  writeValue(obj: any) {
    this.selectedTradePartners = obj;
  }

  ngOnInit() {
    const selectionLimit = this.selectionLimit();
    if (selectionLimit && selectionLimit <= 0) { throw new TypeError("PCPTradePartnerMultiSelectComponent: 'selectionLimit' must be left null for no limit or set greater than 0"); }

    this.tradePartnerOptions = this.tradePartnersToOptions(this._tradePartners);
  }

  isSingleSelect(): boolean {
    return this.selectionLimit() === 1;
  }

  onChange() {
    const sortedTradePartners = this.sortSelectedTop(this._tradePartners, this.selectedTradePartners);
    this.tradePartnerOptions = this.tradePartnersToOptions(sortedTradePartners);
    this.selectedTradePartnersChange.emit(this.selectedTradePartners);
  }

  selectTradePartnerById(id: number) {
    const selectedTradePartner = this._tradePartners.find((tradePartner: Vendor) => tradePartner.id === id);
    if (!selectedTradePartner) { throw new TypeError(`PCPTradePartnerMultiSelectComponent.selectTradePartnerById(${id}) : tradePartner not found.`); }

    if (this.isSingleSelect()) {
      this.clearSelectedTradePartners();
    }

    this.selectTradePartner(selectedTradePartner);
  }

  reloadOptions(): void {
    if (!this.tradePartnersObservable) { return; }

    this.isLoading = true;
    this.tradePartnersObservable
      .pipe(takeUntil(this.ngUnsubscribe$))
      .subscribe((vendors: Vendor[]) => {
        this.tradePartners = vendors;
        this.isLoading = false;
        // TODO: The 'emit' function requires a mandatory any argument
  this.tradePartnersLoaded.emit(null);
      });
  }

  selectAll() {
    this.selectedTradePartners = this.tradePartners;
  }

  clearSelectedTradePartners(): void {
    this.selectedTradePartners = [];
  }

  private selectTradePartner(tradePartner: Vendor) {
    this.selectedTradePartners.push(tradePartner);
  }
}
